// Vendor Imports
import BigNumber from 'bignumber.js';
import _ from 'lodash';
import isEqual from 'lodash/isEqual';
import type React from 'react';
import { useState } from 'react';

// Project Imports
import { ForgeSelect, ForgeCheckbox, ForgeTextField, ForgeIcon } from '@tylertech/forge-react';

import FilterHeader from '../FilterHeader';
import { FilterFooter } from '../FilterFooter';
import { I18n } from 'common/i18n';
import { getPrecision } from 'common/numbers';
import { Key } from 'common/types/keyboard/key';
import { useFocusFirstActionableElement } from './InputFocus';
import { type FilterColumnsMap, type FilterEditorProps } from '../types';
import {
  FILTER_FUNCTION,
  type NoopFilter,
  type NumberFilterArgument,
  type NumberSoqlFilter,
} from '../SoqlFilter';
import * as NumberFilter from '../lib/Filters/NumberFilter';
import * as BaseFilter from '../lib/Filters/BaseFilter';

const requiresRange = (func: string) => _.includes(['rangeInclusive', 'rangeExclusive'], func);

export interface NumberFilterEditorProps extends FilterEditorProps {
  appliedFilter: NumberSoqlFilter | NoopFilter;
  columns: FilterColumnsMap;
}

/**
 * NOTE: Other filter editors store individual UI components' values in state
 * and then apply them to the config separately. Number filter applies the values
 * directly to the filter config, so we store the entire dirty config in state to
 * keep rerenders the same.
 */
function getInitialFilterState(
  appliedFilter: NumberFilterEditorProps['appliedFilter'],
  columns: FilterColumnsMap,
) {
  const initialFilter = appliedFilter;

  // If filter is single value, ensure the function is '=' and that the arguments.value is set.
  if (initialFilter.singleSelect) {
    _.set(initialFilter, 'function', FILTER_FUNCTION.EQUALS);
    _.set(initialFilter, 'arguments.includeNullValues', false);

    const didRequireRange = requiresRange(initialFilter.function);

    if (didRequireRange) {
      const value = _.get(appliedFilter, 'arguments.start', _.toString(NumberFilter.getMinValue(columns)));
      _.set(initialFilter, 'arguments.value', value);
      _.unset(initialFilter, 'arguments.start');
      _.unset(initialFilter, 'arguments.end');
    } else {
      const value = _.get(appliedFilter, 'arguments.value', _.toString(NumberFilter.getMinValue(columns)));
      _.set(initialFilter, 'value', value);
    }
  }

  return initialFilter;
}

export function NumberFilterEditor(props: NumberFilterEditorProps) {
  const { appliedFilter, columns, isReadOnly, onRemove, onUpdate, showRemoveButtonInFooter } = props;

  const [dirtyFilter, setDirtyFilter] = useState<NumberSoqlFilter | NoopFilter>(() =>
    getInitialFilterState(appliedFilter, columns),
  );
  const [prevAppliedFilter, setPrevAppliedFilter] = useState(appliedFilter);
  const numberFilterRef = useFocusFirstActionableElement<HTMLDivElement>();

  if (!isEqual(appliedFilter, prevAppliedFilter)) {
    setPrevAppliedFilter(appliedFilter);
    setDirtyFilter(getInitialFilterState(appliedFilter, columns));
  }

  const onDropdownChange = (newValue: string) => {
    const nowRequiresRange = requiresRange(newValue);
    const didRequireRange = requiresRange(dirtyFilter.function);
    const newFilter = _.cloneDeep(dirtyFilter);

    _.set(newFilter, 'function', newValue);

    if (nowRequiresRange && didRequireRange) {
      newFilter.arguments = dirtyFilter.arguments;
    } else if (nowRequiresRange) {
      const start = _.get(dirtyFilter, 'arguments.value', _.toString(NumberFilter.getMinValue(columns)));
      _.set(newFilter, 'arguments.start', start);
      _.set(newFilter, 'arguments.end', _.toString(NumberFilter.getMaxValue(columns)));
    } else if (didRequireRange) {
      const value = _.get(dirtyFilter, 'arguments.start', _.toString(NumberFilter.getMinValue(columns)));
      _.set(newFilter, 'arguments.value', value);
    } else {
      const value = _.get(dirtyFilter, 'arguments.value', _.toString(NumberFilter.getMinValue(columns)));
      _.set(newFilter, 'arguments.value', value);
    }

    let includeNullValues;
    const filterFunction = newFilter.function;
    if (filterFunction === FILTER_FUNCTION.EQUALS) {
      includeNullValues = false;
    } else if (filterFunction === FILTER_FUNCTION.EXCLUDE_NULL) {
      _.set(newFilter, 'arguments', {});
      includeNullValues = false;
    } else {
      includeNullValues = _.get(appliedFilter, 'arguments.includeNullValues', true);
    }

    _.set(newFilter, 'arguments.includeNullValues', includeNullValues);

    setDirtyFilter(newFilter);
  };

  const onNullCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFilter = _.cloneDeep(dirtyFilter);
    _.set(newFilter, 'arguments.includeNullValues', event.target.checked);

    setDirtyFilter(newFilter);
  };

  const isDirty = () => {
    const isFilterInvalid = !!dirtyFilter.singleSelect && _.isEmpty(dirtyFilter.arguments?.value);
    return !(_.isEqual(dirtyFilter, appliedFilter) || isFilterInvalid);
  };

  const applyFilter = () => {
    const newFilter = _.cloneDeep(dirtyFilter);

    // Swap the start and end if necessary to ensure the range is valid
    const { start, end } = (dirtyFilter.arguments as NumberFilterArgument) || {};

    // Because the filter arguments are always strings, coerce strings to
    // numbers before doing this comparison. For example: start of '24000'
    // and end of '230000' would have caused these to flip.
    if (_.toNumber(start) > _.toNumber(end)) {
      newFilter.arguments = {
        start: end,
        end: start,
      };
    }

    setDirtyFilter(newFilter);
    onUpdate(newFilter);
  };

  const applyOnEnter = (event: React.KeyboardEvent) => {
    if (event.key !== Key.Enter) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if (isDirty()) {
      applyFilter();
    }
  };

  const getStepInterval = (): number | undefined =>
    _.min(_.map([NumberFilter.getMinValue(columns), NumberFilter.getMaxValue(columns)], getPrecision));

  const resetFilter = () => {
    onUpdate(BaseFilter.reset(appliedFilter, columns));
  };

  const formatValue = (value: string | number) => {
    // value is always a string
    if (!NumberFilter.isPercentage(columns)) {
      return value;
    }
    const scale = NumberFilter.getPercentScale(columns);
    return new BigNumber(value).mul(100).div(scale).toString();
  };

  const unformatValue = (value: string) => {
    // value is always a string
    if (!NumberFilter.isPercentage(columns)) {
      return value;
    }
    const scale = NumberFilter.getPercentScale(columns);
    return new BigNumber(value).div(100).mul(scale).toString();
  };

  const onChangeSingleInputField = (newValue: string) => {
    const nextFilter = _.set(_.cloneDeep(dirtyFilter), 'arguments.value', unformatValue(newValue));

    // Default to '=' if no function is set.
    if (dirtyFilter.function === FILTER_FUNCTION.NOOP) {
      _.set(nextFilter, 'function', FILTER_FUNCTION.EQUALS);
    }
    setDirtyFilter(nextFilter);
  };

  const renderRangeInputFields = () => {
    const setArgument = (argument: string, value: string) => {
      const nextFilter = _.cloneDeep(dirtyFilter);
      _.set(nextFilter, ['arguments', argument], unformatValue(value));
      setDirtyFilter(nextFilter);
    };

    const start = formatValue(dirtyFilter.arguments?.start ?? (NumberFilter.getMinValue(columns) || ''));
    const end = formatValue(dirtyFilter.arguments?.end ?? (NumberFilter.getMaxValue(columns) || ''));

    return (
      <div className="number-filter-range-two-inputs-container">
        <ForgeTextField density="small">
          <input
            aria-label={I18n.t('shared.components.filter_bar.from')}
            className="range-input"
            data-testid="start"
            id="start"
            onChange={(event) => {
              setArgument('start', event.target.value);
            }}
            onKeyUp={(event: React.KeyboardEvent) => applyOnEnter(event)}
            placeholder={I18n.t('shared.components.filter_bar.from')}
            step={getStepInterval()}
            type="number"
            value={start}
          />
        </ForgeTextField>
        <span>-</span>
        <ForgeTextField density="small">
          <input
            aria-label={I18n.t('shared.components.filter_bar.to')}
            className="range-input"
            data-testid="end"
            id="end"
            onChange={(event) => {
              setArgument('end', event.target.value);
            }}
            onKeyUp={(event: React.KeyboardEvent) => applyOnEnter(event)}
            placeholder={I18n.t('shared.components.filter_bar.to')}
            step={getStepInterval()}
            type="number"
            value={end}
          />
        </ForgeTextField>
      </div>
    );
  };

  const renderSingleInputField = () => {
    const value = dirtyFilter.arguments?.value ?? '';

    return (
      <ForgeTextField className="number-filter-range-container" density="small">
        <input
          aria-label={I18n.t('shared.components.filter_bar.range_filter.value')}
          className="range-input"
          data-testid="value"
          id="value"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onChangeSingleInputField(event?.target.value);
          }}
          onKeyUp={(event: React.KeyboardEvent) => applyOnEnter(event)}
          placeholder={I18n.t('shared.components.filter_bar.range_filter.value')}
          step={getStepInterval()}
          type="number"
          value={formatValue(value)}
        />
      </ForgeTextField>
    );
  };

  const shouldRenderInputFields = () => {
    if (dirtyFilter.singleSelect) {
      return true;
    }

    const filterFunction = dirtyFilter.function;
    if (!filterFunction) {
      return false;
    }
    switch (filterFunction) {
      case FILTER_FUNCTION.EXCLUDE_NULL:
        return false;
      default:
        return true;
    }
  };

  const renderInputFields = () => {
    if (dirtyFilter.singleSelect) {
      return renderSingleInputField();
    }

    return requiresRange(dirtyFilter.function) ? renderRangeInputFields() : renderSingleInputField();
  };

  const renderDropdown = () => {
    const operators = 'shared.components.filter_bar.range_filter.operators';
    const defaultValue =
      dirtyFilter.function === FILTER_FUNCTION.NOOP ? FILTER_FUNCTION.EQUALS : dirtyFilter.function;

    return (
      <ForgeSelect
        onChange={(event: CustomEvent) => onDropdownChange(event.detail)}
        options={[
          {
            label: I18n.t(`${operators}.equal.title`),
            value: '=',
          },
          {
            label: I18n.t(`${operators}.not_equal.title`),
            value: '!=',
          },
          {
            label: I18n.t(`${operators}.less_than.title`),
            value: '<',
          },
          {
            label: I18n.t(`${operators}.greater_than.title`),
            value: '>',
          },
          {
            label: I18n.t(`${operators}.less_than_or_equal.title`),
            value: '<=',
          },
          {
            label: I18n.t(`${operators}.greater_than_or_equal.title`),
            value: '>=',
          },
          {
            label: I18n.t(`${operators}.range_exclusive`),
            value: 'rangeExclusive',
          },
          {
            label: I18n.t(`${operators}.range_inclusive`),
            value: 'rangeInclusive',
          },
          { label: I18n.t(`${operators}.exclude_null`), value: 'excludeNull' },
        ]}
        aria-label={I18n.t('shared.components.filter_bar.range_filter.label')}
        variant="plain"
        theme="primary"
        popoverIcon={false}
        density="small"
        value={defaultValue}
      >
        <ForgeIcon slot="end" name="chevron_down" theme="primary" icon_type="button" />
      </ForgeSelect>
    );
  };

  const shouldRenderDropdown = () => !dirtyFilter.singleSelect;

  const shouldRenderNullValueCheckbox = () => {
    if (dirtyFilter.singleSelect) {
      return false;
    }
    const filterFunction = dirtyFilter.function;
    if (!filterFunction) {
      return false;
    }
    switch (filterFunction) {
      case FILTER_FUNCTION.NOOP:
      case FILTER_FUNCTION.EXCLUDE_NULL:
      case FILTER_FUNCTION.EQUALS:
        return false;
      default:
        return true;
    }
  };

  const renderNullValueCheckbox = () => {
    const checked = _.get(dirtyFilter, 'arguments.includeNullValues', true);
    const nullToggleId = _.uniqueId('include-nulls-');

    return (
      <ForgeCheckbox
        className="include-nulls-checkbox"
        density="small"
        id={nullToggleId}
        onChange={onNullCheckboxChange}
        checked={checked}
      >
        {I18n.t('shared.components.filter_bar.range_filter.include_null_values')}
      </ForgeCheckbox>
    );
  };

  return (
    <div className="filter-controls number-filter" ref={numberFilterRef}>
      <div className="number-filter-container">
        <FilterHeader name={BaseFilter.getFilterName(appliedFilter, columns)}>
          {shouldRenderDropdown() && renderDropdown()}
        </FilterHeader>
        {shouldRenderInputFields() && renderInputFields()}
        {shouldRenderNullValueCheckbox() && renderNullValueCheckbox()}
      </div>
      <FilterFooter
        disableApplyFilter={!isDirty()}
        isDrilldown={dirtyFilter.isDrilldown}
        isReadOnly={isReadOnly}
        onClickApply={applyFilter}
        onClickRemove={onRemove}
        onClickReset={resetFilter}
        showRemoveButton={showRemoveButtonInFooter}
      />
    </div>
  );
}
